import React, { useCallback, useEffect, useRef, useState } from 'react';
import SNInput from '@luna-sena/sena-input';
import apiManager from '@/api';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';
import './wedriveFinder.css';

// ─── 타입 ──────────────────────────────────────────────────────────────────────

interface DriveItem {
  fileUniqueKey: string;
  parentFileUniqueKey: string;
  fileName: string;
  extentionType: string;
  mimeType: string;
  folderType: string | null;
  bizId: string;
  path: string;
  directory: boolean;
  lock: boolean;
  bizShareFolder: boolean;
  size: string;
  _size: number;
  uploadDate: string;
  lastModifyDate: string;
  thumbNailPath: string | null;
  [key: string]: unknown;
}

interface NavEntry {
  directoryPath: string;
  fileUniqueKey: string;
  parentFileUniqueKey: string;
  bizShareFolder: boolean;
  folderName: string;
}

export interface WeDriveFileInfo {
  fileUniqueKey: string;
  fileName: string;
  fileSize: number;
  extentionType: string;
}

interface WeDriveFinderV2Props {
  onClose: () => void;
  onConfirm: (files: WeDriveFileInfo[]) => void;
  fileExtensions?: string[];
}

// ─── 아이콘 URL ────────────────────────────────────────────────────────────────

const ICON_BASE  = 'https://static.wehago.com/imgs/common/icon_file/32x32';
const THUMB_BASE = 'https://static.wehago.com/imgs/common/icon_file/52x41';

const ICON_EXTS = new Set([
  'ai', 'doc', 'docx', 'jpg', 'hwp', 'gif', 'png',
  'mp3', 'mp4', 'zip', 'wma', 'mpg', 'avi', 'pdf',
  'ppt', 'pptx', 'xls', 'xlsx', 'bmp',
]);

function getIconSrc(item: DriveItem): string {
  if (item.directory) {
    return item.lock ? `${ICON_BASE}/ico_folder_secure_35.png` : `${ICON_BASE}/ico_folder_35.png`;
  }
  const ext = (item.extentionType ?? '').toLowerCase();
  return ICON_EXTS.has(ext) ? `${ICON_BASE}/ico_${ext}_32.png` : `${ICON_BASE}/ico_file_etc_32.png`;
}

function getThumbSrc(item: DriveItem): string {
  if (item.directory) {
    return item.lock ? `${THUMB_BASE}/ico_folder_secure_52.png` : `${THUMB_BASE}/ico_folder_52.png`;
  }
  if (item.thumbNailPath) return item.thumbNailPath;
  const ext = (item.extentionType ?? '').toLowerCase();
  return ICON_EXTS.has(ext) ? `${THUMB_BASE}/ico_${ext}_52.png` : `${THUMB_BASE}/ico_file_etc_52.png`;
}

// ─── 카드뷰 정렬 레이블 ────────────────────────────────────────────────────────

const SORT_LABEL_MAP: Record<string, string> = {
  extentionType:  '종류',
  fileName:       '이름',
  fileSize:       '크기',
  uploadDate:     '업로드 날짜',
  lastModifyDate: '수정 날짜',
};

// ─── 브레드크럼 파싱 ────────────────────────────────────────────────────────────

function parseBreadcrumb(directoryPath: string, userId: string): string[] {
  const path = directoryPath.replace('//', '/');
  const parts = path.split('/').filter(Boolean);
  const rootId = userId.replace('@', '');
  if (parts[0] === userId || parts[0] === rootId) {
    return ['WE드라이브', ...parts.slice(1)];
  }
  return ['WE드라이브', ...parts];
}

// ─── 잠금 서브 다이얼로그 (공통 껍데기) ──────────────────────────────────────────

function SubDialog({ width, onClose, title, children }: { width: number; onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <div className="LUX_basic_dialog">
      <div className="dimmed" onClick={onClose} />
      <div className="dialog_wrap_tbl">
        <div className="dialog_wrap_tblcel">
          <div className="dialog_wrap" style={{ border: '0', borderRadius: 20, overflow: 'hidden' }}>
            <div className="dialog_content rtc_dialog roundstyle notosans_font" style={{ width }}>
              <div className="dialog_data">
                <div className="dialog_data_tit">
                  <h1 className="txtcnt">{title}</h1>
                  <button type="button" className="LUX_basic_btn btn_clr" onClick={onClose}>
                    <span className="sp_lux">닫기</span>
                  </button>
                </div>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────

function WeDriveFinderV2({ onClose, onConfirm, fileExtensions }: WeDriveFinderV2Props) {
  const local = useAppSelector((s: RootState) => s.local);
  const dialogRef = useRef<HTMLDivElement>(null);

  // 서버 상태
  const [isLoading, setIsLoading]                     = useState(false);
  const [indexUseMode, setIndexUseMode]               = useState('true');
  const [tokenId, setTokenId]                         = useState('');
  const [userId, setUserId]                           = useState('');
  const [sortName, setSortName]                       = useState('fileName');
  const [sortType, setSortType]                       = useState('1');
  const [viewType, setViewType]                       = useState(0);
  const [isThumbSortOpen, setIsThumbSortOpen]         = useState(false);
  const [directoryPath, setDirectoryPath]             = useState('');
  const [fileUniqueKey, setFileUniqueKey]             = useState('');
  const [parentFileUniqueKey, setParentFileUniqueKey] = useState('');
  const [bizShareFolder, setBizShareFolder]           = useState(false);
  const [storageList, setStorageList]                 = useState<DriveItem[]>([]);
  const [storageListCnt, setStorageListCnt]           = useState(0);
  const [totalPageCnt, setTotalPageCnt]               = useState(1);
  const [currentPage, setCurrentPage]                 = useState(1);
  const [isSearchView, setIsSearchView]               = useState(false);
  const [navStack, setNavStack]                       = useState<NavEntry[]>([]);
  const [selectedKeys, setSelectedKeys]               = useState<string[]>([]);
  const [searchText, setSearchText]                   = useState('');
  const [isSearchMode, setIsSearchMode]               = useState(false);

  // 잠금 비밀번호
  const [isLockPWOpen, setIsLockPWOpen]     = useState(false);
  const [lockFolderIdx, setLockFolderIdx]   = useState(0);
  const [lockFolderKey, setLockFolderKey]   = useState('');
  const [lockPWValue, setLockPWValue]       = useState('');
  const [lockPWError, setLockPWError]       = useState('');
  const [isLockFindOpen, setIsLockFindOpen] = useState(false);
  const [wehagoPW, setWehagoPW]             = useState('');
  const [wehagoPWError, setWehagoPWError]   = useState('');

  // ── 초기화 ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    initDrive();
  }, []);

  const initDrive = async () => {
    setIsLoading(true);
    try {
      const result = await apiManager.getWedriveInitInfo();
      if (result.resultCode !== 200 || !result.resultData) throw new Error('init failed');
      const info = result.resultData as Record<string, unknown>;

      const iMode = String(info.indexUseMode ?? 'true');
      const tId   = String(info.weDriveTokenId ?? '');
      const uId   = String(info.userId ?? '');
      const sName = String(info.sortName ?? 'fileName');
      const sType = String(info.sortType ?? '1');
      const dPath = String(info.directoryPath ?? '');
      const fKey  = String(info.fileUniqueKey ?? '');

      setIndexUseMode(iMode);
      setTokenId(tId);
      setUserId(uId);
      setSortName(sName);
      setSortType(sType);
      setDirectoryPath(dPath);
      setFileUniqueKey(fKey);
      setParentFileUniqueKey(fKey);

      await fetchFileList({
        indexUseMode: iMode, tokenId: tId, userId: uId,
        directoryPath: dPath, fileUniqueKey: fKey, parentFileUniqueKey: fKey,
        viewType: Number(info.viewType ?? 0),
        sortName: sName, sortType: sType, currentPage: 1, bizShareFolder: false,
      });
    } catch {
      setIsLoading(false);
    }
  };

  // ── 파일 목록 조회 ───────────────────────────────────────────────────────────

  interface FetchParams {
    indexUseMode: string; tokenId: string; userId: string;
    directoryPath: string; fileUniqueKey: string; parentFileUniqueKey: string;
    viewType: number; sortName: string; sortType: string;
    currentPage: number; bizShareFolder: boolean;
  }

  const fetchFileList = async (params: FetchParams) => {
    setSelectedKeys([]);
    setIsLoading(true);
    try {
      const result = await apiManager.getWedriveFileList({
        ...params, cno: apiManager.selectedCno, ccode: apiManager.selectedCCode,
      });
      if (result.resultCode !== 200) throw new Error('list failed');
      const data = result.resultData as Record<string, unknown>;
      setStorageList((data.storageList as DriveItem[]) ?? []);
      setStorageListCnt((data.storageListCnt as number) ?? 0);
      setTotalPageCnt((data.totalPageCnt as number) ?? 1);
      setCurrentPage((data.currentPage as number) ?? 1);
      setIsSearchView((data.isSearchView as boolean) ?? false);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const buildFetchParams = (overrides: Partial<FetchParams> = {}): FetchParams => ({
    indexUseMode, tokenId, userId, directoryPath, fileUniqueKey,
    parentFileUniqueKey, viewType, sortName, sortType, currentPage, bizShareFolder,
    ...overrides,
  });

  // ── 폴더 열기 ────────────────────────────────────────────────────────────────

  const handleFolderOpen = (idx: number, isPassLock: boolean) => {
    const item = storageList[idx];
    if (!item.directory) return;

    if (item.lock && !isPassLock) {
      setLockFolderIdx(idx);
      setLockFolderKey(item.fileUniqueKey);
      setLockPWValue('');
      setLockPWError('');
      setIsLockPWOpen(true);
      return;
    }

    setNavStack((prev) => [
      ...prev,
      { directoryPath, fileUniqueKey, parentFileUniqueKey, bizShareFolder, folderName: item.fileName },
    ]);
    setDirectoryPath(item.path);
    setFileUniqueKey(item.fileUniqueKey);
    setParentFileUniqueKey(String(item.parentFileUniqueKey));
    setBizShareFolder(item.bizShareFolder);
    fetchFileList(buildFetchParams({
      directoryPath: item.path, fileUniqueKey: item.fileUniqueKey,
      parentFileUniqueKey: String(item.parentFileUniqueKey),
      bizShareFolder: item.bizShareFolder, currentPage: 1,
    }));
  };

  // ── 뒤로가기 ─────────────────────────────────────────────────────────────────

  const handleGoBack = () => {
    if (navStack.length === 0) return;
    const prev = navStack[navStack.length - 1];
    setNavStack((stack) => stack.slice(0, -1));
    setDirectoryPath(prev.directoryPath);
    setFileUniqueKey(prev.fileUniqueKey);
    setParentFileUniqueKey(prev.parentFileUniqueKey);
    setBizShareFolder(prev.bizShareFolder);
    fetchFileList(buildFetchParams({
      directoryPath: prev.directoryPath, fileUniqueKey: prev.fileUniqueKey,
      parentFileUniqueKey: prev.parentFileUniqueKey,
      bizShareFolder: prev.bizShareFolder, currentPage: 1,
    }));
  };

  // ── 정렬 ─────────────────────────────────────────────────────────────────────

  const handleSort = (newSortName: string) => {
    let newSortType = sortType;
    if (sortName === newSortName) {
      newSortType = sortType === '1' ? '0' : '1';
      setSortType(newSortType);
    } else {
      setSortName(newSortName);
      newSortType = '1';
      setSortType('1');
    }
    if (isSearchMode) {
      fetchSearch({ sortName: newSortName, sortType: newSortType, currentPage: 1 });
    } else {
      fetchFileList(buildFetchParams({ sortName: newSortName, sortType: newSortType, currentPage: 1 }));
    }
  };

  // ── 페이지 변경 ───────────────────────────────────────────────────────────────

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;
    if (isSearchMode) fetchSearch({ currentPage: page });
    else fetchFileList(buildFetchParams({ currentPage: page }));
  };

  // ── 검색 ─────────────────────────────────────────────────────────────────────

  const fetchSearch = async (overrides: { sortName?: string; sortType?: string; currentPage?: number } = {}) => {
    setSelectedKeys([]);
    setIsLoading(true);
    try {
      const result = await apiManager.getWedriveSearchKeyword(searchText, {
        tokenId, sortName: overrides.sortName ?? sortName,
        sortType: overrides.sortType ?? sortType,
        currentPage: overrides.currentPage ?? currentPage,
        indexUseMode, cno: apiManager.selectedCno, ccode: apiManager.selectedCCode,
      });
      if (result.resultCode !== 200) throw new Error('search failed');
      const data = result.resultData as Record<string, unknown>;
      setStorageList((data.storageList as DriveItem[]) ?? []);
      setStorageListCnt((data.storageListCnt as number) ?? 0);
      setTotalPageCnt((data.totalPageCnt as number) ?? 1);
      setCurrentPage((data.currentPage as number) ?? 1);
      setIsSearchView((data.isSearchView as boolean) ?? true);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchText.trim()) { setIsSearchMode(false); initDrive(); return; }
    if (searchText.trim().length < 2) return;
    setIsSearchMode(true);
    fetchSearch({ currentPage: 1 });
  };

  const handleSearchClear = () => {
    setSearchText('');
    setIsSearchMode(false);
    initDrive();
  };

  // ── 확인 ─────────────────────────────────────────────────────────────────────

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleConfirm = () => {
    if (selectedKeys.length === 0) return;
    const files = selectedKeys
      .map((key) => storageList.find((i) => i.fileUniqueKey === key))
      .filter((item): item is DriveItem => !!item && item._size > 0)
      .filter((item) => !fileExtensions || fileExtensions.includes(item.extentionType))
      .map((item) => ({ fileUniqueKey: item.fileUniqueKey, fileName: item.fileName, fileSize: item._size, extentionType: item.extentionType }));
    if (files.length === 0) return;
    onConfirm(files);
  };

  // ── 잠금 비밀번호 확인 ────────────────────────────────────────────────────────

  const handleLockPWCheck = async () => {
    if (!lockPWValue.trim()) { setLockPWError('비밀번호를 입력해주세요.'); return; }
    setIsLoading(true);
    try {
      const res = await apiManager.getWedriveLockCheck(tokenId, lockPWValue, lockFolderKey);
      if (res.resultCode === '0000') {
        setIsLockPWOpen(false);
        handleFolderOpen(lockFolderIdx, true);
      } else {
        setLockPWError(res.serverMsg ?? '비밀번호가 일치하지 않습니다.');
      }
    } catch {
      setLockPWError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── 잠금 비밀번호 찾기 ────────────────────────────────────────────────────────

  const handleLockPWFind = async () => {
    if (!wehagoPW.trim()) { setWehagoPWError('위하고 비밀번호를 입력해주세요.'); return; }
    setIsLoading(true);
    try {
      const loginRes = await apiManager.getWedriveCheckLoginPassword(tokenId, wehagoPW);
      if (loginRes.resultCode !== '0000') {
        setWehagoPWError(loginRes.serverMsg ?? '비밀번호가 일치하지 않습니다.');
        return;
      }
      const findRes = await apiManager.getWedriveGetLockFolderPassword(
        tokenId, lockFolderKey, local.userId ?? '', local.userName ?? '',
      );
      if (findRes.resultCode === '0000') {
        setIsLockFindOpen(false);
      } else {
        setWehagoPWError(findRes.serverMsg ?? '오류가 발생했습니다.');
      }
    } catch {
      setWehagoPWError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Escape 키 / 외부 클릭 ────────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleClickOutside = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) onClose();
  }, [onClose]);

  // ── 브레드크럼 ───────────────────────────────────────────────────────────────

  const breadcrumb = directoryPath ? parseBreadcrumb(directoryPath, userId) : ['WE드라이브'];

  // ── 잠금 비밀번호 다이얼로그 ──────────────────────────────────────────────────

  if (isLockPWOpen) {
    return (
      <SubDialog width={420} title="잠금 폴더 비밀번호" onClose={() => setIsLockPWOpen(false)}>
        <div className="dialog_data_area noline">
          <div style={{ padding: '8px 0' }}>
            <p style={{ textAlign: 'center', marginBottom: 12, lineHeight: 1.6 }}>
              잠금 폴더에 접근하려면 비밀번호가 필요합니다.
            </p>
            <dl style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <dt style={{ fontSize: 13, fontWeight: 500 }}>비밀번호</dt>
              <dd style={{ margin: 0 }}>
                <input
                  type="password"
                  className="rtcDialogCreate__input"
                  style={{ width: '100%' }}
                  autoFocus
                  value={lockPWValue}
                  onChange={(e) => { setLockPWValue(e.target.value); setLockPWError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLockPWCheck(); }}
                />
                {lockPWError && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>{lockPWError}</p>}
              </dd>
            </dl>
            <button
              type="button"
              style={{ marginTop: 8, fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => { setIsLockPWOpen(false); setIsLockFindOpen(true); setWehagoPW(''); setWehagoPWError(''); }}
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>
        </div>
        <div className="dialog_btnbx">
          <div>
            <button type="button" className="common_round_btn rtcNew_btnType" onClick={() => setIsLockPWOpen(false)}>취소</button>
            <button type="button" className="common_round_btn rtcNew_btnType blue" disabled={isLoading} onClick={handleLockPWCheck}>
              {isLoading ? '처리 중...' : '확인'}
            </button>
          </div>
        </div>
      </SubDialog>
    );
  }

  // ── 잠금 비밀번호 찾기 다이얼로그 ────────────────────────────────────────────

  if (isLockFindOpen) {
    return (
      <SubDialog width={420} title="비밀번호 찾기" onClose={() => setIsLockFindOpen(false)}>
        <div className="dialog_data_area noline">
          <div style={{ padding: '8px 0' }}>
            <p style={{ textAlign: 'center', marginBottom: 12, lineHeight: 1.6 }}>
              위하고 비밀번호를 입력하시면<br />잠금 비밀번호를 이메일로 전송합니다.
            </p>
            <dl style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <dt style={{ fontSize: 13, fontWeight: 500 }}>아이디</dt>
              <dd style={{ margin: '0 0 8px', fontSize: 13, color: '#555' }}>
                {userId.endsWith('@') ? userId.slice(0, -1) : userId}
              </dd>
              <dt style={{ fontSize: 13, fontWeight: 500 }}>위하고 비밀번호</dt>
              <dd style={{ margin: 0 }}>
                <input
                  type="password"
                  className="rtcDialogCreate__input"
                  style={{ width: '100%' }}
                  autoFocus
                  value={wehagoPW}
                  onChange={(e) => { setWehagoPW(e.target.value); setWehagoPWError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLockPWFind(); }}
                />
                {wehagoPWError && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>{wehagoPWError}</p>}
              </dd>
            </dl>
          </div>
        </div>
        <div className="dialog_btnbx">
          <div>
            <button type="button" className="common_round_btn rtcNew_btnType" onClick={() => setIsLockFindOpen(false)}>취소</button>
            <button type="button" className="common_round_btn rtcNew_btnType blue" disabled={isLoading} onClick={handleLockPWFind}>
              {isLoading ? '처리 중...' : '비밀번호 찾기'}
            </button>
          </div>
        </div>
      </SubDialog>
    );
  }

  // ── 메인 다이얼로그 ───────────────────────────────────────────────────────────

  return (
    <div className="LUX_basic_dialog">
      <div className="dimmed" onClick={handleClickOutside} />
      <div className="dialog_wrap_tbl" ref={dialogRef}>
        <div className="dialog_wrap_tblcel">
          <div className="dialog_wrap" style={{ border: '0', borderRadius: '20px', overflow: 'hidden' }}>
            <div className="dialog_content rtc_dialog roundstyle notosans_font wd_dialog" style={{ width: 800 }}>
              <div className="dialog_data">

                {/* 헤더 */}
                <div className="dialog_data_tit">
                  <h1 className="txtcnt">WE드라이브</h1>
                  <button type="button" className="LUX_basic_btn btn_clr" onClick={onClose}>
                    <span className="sp_lux">닫기</span>
                  </button>
                </div>

                {/* 콘텐츠 */}
                <div className="dialog_data_area noline">
                  <div className="dialog_data_section wd_07">

                    {/* 상단: 경로 + 검색 + 뷰 토글 */}
                    <div className="list_title_box">

                      {/* 브레드크럼 */}
                      <ul className="file_root" id="path_item2">
                        {isSearchView ? (
                          <li className="item"><a>검색 결과 ({storageListCnt})</a></li>
                        ) : (
                          breadcrumb.map((seg, i) => (
                            <li key={i} className="item">
                              <a className={`item_text${i === breadcrumb.length - 1 ? ' text_blue' : ''}`}>{seg}</a>
                            </li>
                          ))
                        )}
                      </ul>

                      {/* 검색 + 뷰 토글 */}
                      <div className="edit_box">
                        <div>
                          <SNInput
                            type="search"
                            hintText="검색어를 입력하세요."
                            value={searchText}
                            onChange={(e) => setSearchText((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                            styles={{ inputBox: { width: 220, height: 28 }, input: { fontSize: 13 } }}
                          />
                          {isSearchMode && (
                            <button
                              type="button"
                              className="LUX_basic_btn Default basic LUX_renewal"
                              style={{ height: 28, padding: '0 10px', fontSize: 12, marginLeft: 4 }}
                              onClick={handleSearchClear}
                            >
                              초기화
                            </button>
                          )}
                        </div>
                        <div className="btn_toggle_box">
                          <button
                            type="button"
                            className={`btn_toggle${viewType === 1 ? ' active' : ''}`}
                            onClick={() => setViewType(1)}
                          />
                          <button
                            type="button"
                            className={`btn_toggle type_list${viewType === 0 ? ' active' : ''}`}
                            onClick={() => setViewType(0)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ── 리스트뷰 ── */}
                    {viewType === 0 ? (
                      <div>
                        <div className="list_contnet_box">
                          <div className="common_boxtbl style_basic tbl_wd_07">

                            {/* 헤더 */}
                            <div className="list_header clearfix">
                              <div className="col1" />
                              <div className="col2">종류</div>
                              <div className={`col3${sortName === 'fileName' ? ` active ${sortType === '1' ? 'up' : 'down'}` : ''}`}>
                                이름
                                <button type="button" className="btn_sort" onClick={() => handleSort('fileName')} />
                              </div>
                              <div className={`col4${sortName === 'fileSize' ? ` active ${sortType === '1' ? 'up' : 'down'}` : ''}`}>
                                크기
                                <button type="button" className="btn_sort" onClick={() => handleSort('fileSize')} />
                              </div>
                              <div className={`col5${sortName === 'uploadDate' ? ` active ${sortType === '1' ? 'up' : 'down'}` : ''}`}>
                                업로드 날짜
                                <button type="button" className="btn_sort" onClick={() => handleSort('uploadDate')} />
                              </div>
                              <div className={`col6${sortName === 'lastModifyDate' ? ` active ${sortType === '1' ? 'up' : 'down'}` : ''}`}>
                                수정 날짜
                                <button type="button" className="btn_sort" onClick={() => handleSort('lastModifyDate')} />
                              </div>
                            </div>

                            {/* 본문 */}
                            <div className="list_body" id="WeDriveTableList">

                              {/* 뒤로가기 행 */}
                              {navStack.length > 0 && !isSearchView && (
                                <div className="row" style={{ cursor: 'pointer' }} onClick={handleGoBack}>
                                  <div className="col1" />
                                  <div className="col2">
                                    <img src={`${ICON_BASE}/ico_folder_35.png`} alt="뒤로가기" width={38} height={38} />
                                  </div>
                                  <div className="col3"><p className="ellipsis">..</p></div>
                                  <div className="col4"><p className="ellipsis" /></div>
                                  <div className="col5"><p className="ellipsis" /></div>
                                  <div className="col6"><p className="ellipsis" /></div>
                                </div>
                              )}

                              {isLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 13 }}>불러오는 중...</div>
                              ) : storageList.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb', fontSize: 13 }}>표시할 내용이 없습니다.</div>
                              ) : storageList.map((item, idx) => {
                                const isSelected = selectedKeys.includes(item.fileUniqueKey);
                                const iconSrc = getIconSrc(item);
                                return (
                                  <div
                                    key={item.fileUniqueKey}
                                    className={`row${isSelected ? ' checked' : ''}`}
                                    style={{ cursor: item.directory ? 'pointer' : 'default' }}
                                    onClick={() => { if (item.directory) handleFolderOpen(idx, false); }}
                                  >
                                    {/* col1: 체크박스 */}
                                    <div className="col1" onClick={(e) => e.stopPropagation()}>
                                      {!item.directory && (
                                        <span className="LUX_basic_switch LUX_renewal" style={{ width: 15, height: 30 }}>
                                          <span className="LUXckbx">
                                            <input
                                              type="checkbox"
                                              id={`WD-V2-check-${idx}`}
                                              style={{ cursor: 'pointer' }}
                                              checked={isSelected}
                                              readOnly
                                              onClick={(e) => { e.stopPropagation(); toggleSelect(item.fileUniqueKey); }}
                                            />
                                            <span
                                              className="sp_lux"
                                              onClick={(e) => { e.stopPropagation(); toggleSelect(item.fileUniqueKey); }}
                                            />
                                          </span>
                                        </span>
                                      )}
                                    </div>
                                    {/* col2: 아이콘 */}
                                    <div className="col2">
                                      <img src={iconSrc} alt={item.fileName} width={38} height={38} />
                                    </div>
                                    {/* col3: 이름 */}
                                    <div className="col3"><p className="ellipsis">{item.fileName}</p></div>
                                    {/* col4: 크기 */}
                                    <div className="col4"><p className="ellipsis">{!item.directory ? item.size : ''}</p></div>
                                    {/* col5: 업로드 날짜 */}
                                    <div className="col5"><p className="ellipsis">{item.uploadDate ?? ''}</p></div>
                                    {/* col6: 수정 날짜 */}
                                    <div className="col6"><p className="ellipsis">{item.lastModifyDate ?? ''}</p></div>
                                  </div>
                                );
                              })}
                            </div>

                          </div>
                        </div>
                      </div>
                    ) : (

                    /* ── 카드뷰 ── */
                    <div className="card_view_list">
                      {/* 카드뷰 헤더 */}
                      <div className="view_header">
                        <div className="col1" />
                        &nbsp;
                        <div className="col2">
                          <div className="sort_box">
                            <button
                              type="button"
                              className="btn_sort"
                              onClick={() => setIsThumbSortOpen((v) => !v)}
                            >
                              {SORT_LABEL_MAP[sortName] ?? '이름'}
                            </button>
                            <ul className="sort_list" id="WD-thumbViewSortMenu" style={{ display: isThumbSortOpen ? 'block' : 'none' }}>
                              {Object.entries(SORT_LABEL_MAP).map(([key, label]) => (
                                <li key={key} className="item">
                                  <button type="button" onClick={() => { handleSort(key); setIsThumbSortOpen(false); }}>
                                    {label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <button
                            type="button"
                            className={`btn_arrow${sortType === '1' ? ' up' : ' down'}`}
                            onClick={() => {
                              const next = sortType === '1' ? '0' : '1';
                              setSortType(next);
                              if (isSearchMode) fetchSearch({ sortType: next, currentPage: 1 });
                              else fetchFileList(buildFetchParams({ sortType: next, currentPage: 1 }));
                            }}
                          />
                        </div>
                      </div>

                      {/* 카드뷰 본문 */}
                      <div className="view_body" id="WeDriveTableList">
                        {isLoading ? (
                          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999', fontSize: 13 }}>불러오는 중...</div>
                        ) : storageList.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb', fontSize: 13 }}>표시할 내용이 없습니다.</div>
                        ) : (
                          <ul className="view_list type_old">
                            {/* 뒤로가기 */}
                            {navStack.length > 0 && !isSearchView && (
                              <li className="item" onClick={handleGoBack} style={{ cursor: 'pointer' }}>
                                <a href="javascript:void(0)">
                                  <div className="thumimage_box">
                                    <span className="img_bx" title="뒤로가기">
                                      <img src={`${THUMB_BASE}/ico_folder_52.png`} />
                                    </span>
                                  </div>
                                  <div className="file_name"><span>..</span></div>
                                </a>
                              </li>
                            )}
                            {storageList.map((item, idx) => {
                              const isSelected = selectedKeys.includes(item.fileUniqueKey);
                              const thumbSrc = getThumbSrc(item);
                              return (
                                <li key={item.fileUniqueKey} className="item">
                                  <a
                                    href="javascript:void(0)"
                                    onClick={(e) => { if (item.directory) handleFolderOpen(idx, false); else e.preventDefault(); }}
                                  >
                                    <div className="thumimage_box">
                                      {!item.directory && (
                                        <span
                                          className="LUX_basic_switch LUX_renewal"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span className="LUXckbx">
                                            <input
                                              type="checkbox"
                                              id={`WD-V2-check-card-${idx}`}
                                              style={{ cursor: 'pointer' }}
                                              checked={isSelected}
                                              readOnly
                                              onClick={(e) => { e.stopPropagation(); toggleSelect(item.fileUniqueKey); }}
                                            />
                                            <span
                                              className="sp_lux"
                                              onClick={(e) => { e.stopPropagation(); toggleSelect(item.fileUniqueKey); }}
                                            />
                                          </span>
                                        </span>
                                      )}
                                      <span className="img_bx" title={item.fileName}>
                                        <img src={thumbSrc} />
                                      </span>
                                    </div>
                                    <div className="file_name">
                                      <span title={item.fileName}>{item.fileName}</span>
                                    </div>
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                    )}

                    {/* 페이징 */}
                    {totalPageCnt > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13 }}>
                        <button
                          type="button"
                          className="LUX_basic_btn Default basic LUX_renewal"
                          style={{ height: 26, padding: '0 10px', fontSize: 12 }}
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        >이전</button>
                        <span style={{ color: '#555' }}>{currentPage} / {totalPageCnt}</span>
                        <button
                          type="button"
                          className="LUX_basic_btn Default basic LUX_renewal"
                          style={{ height: 26, padding: '0 10px', fontSize: 12 }}
                          disabled={currentPage >= totalPageCnt}
                          onClick={() => handlePageChange(currentPage + 1)}
                        >다음</button>
                      </div>
                    )}

                  </div>
                </div>

                {/* 푸터 */}
                <div className="dialog_btnbx">
                  <div>
                    {selectedKeys.length > 0 && (
                      <span style={{ marginRight: 8, fontSize: 13, color: '#555' }}>{selectedKeys.length}개 선택됨</span>
                    )}
                    <button type="button" className="common_round_btn rtcNew_btnType" onClick={onClose}>취소</button>
                    <button
                      type="button"
                      className={`common_round_btn rtcNew_btnType blue${selectedKeys.length === 0 ? ' disabled' : ''}`}
                      disabled={selectedKeys.length === 0}
                      onClick={handleConfirm}
                    >
                      확인
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeDriveFinderV2;
